import { useAnalysisStore } from '@modules/analysis/store/analysisStore';
import { EditReason } from '@modules/charts/components/ChartTooltipWrapper/ChartTooltipActions/EditReason';
import { MergeWithAnotherReason } from '@modules/charts/components/ChartTooltipWrapper/ChartTooltipActions/MergeWithAnotherReason';
import { useChartTooltipStore } from '@modules/charts/components/ChartTooltipWrapper/hooks';
import { useMergeSuggestions } from '@modules/charts/components/ChartTooltipWrapper/hooks/useMergeSuggestions';
import { TooltipPosition } from '@modules/charts/types';
import { getChartLabelIdOrName } from '@modules/charts/utils';
import { EqlFieldName } from '@modules/common/constants';
import { GetChartLabelOptions } from '@modules/common/types';
import { getFieldNameFromEqlField } from '@modules/common/utils';
import { CompareAddToFilterAction } from '@modules/quantify/components/CompareChartTooltip/CompareAddToFilterAction';
import { CompareChartTooltipDelta } from '@modules/quantify/components/CompareChartTooltip/CompareChartTooltipDelta';
import { CompareChartTooltipRecordsCount } from '@modules/quantify/components/CompareChartTooltip/CompareChartTooltipRecordsCount';
import { CompareChartTootlipLabel } from '@modules/quantify/components/CompareChartTooltip/CompareChartTootlipLabel';
import { QuantifyFeedbackListDrawer } from '@modules/quantify/components/QuantifyFeedbackListDrawer';
import {
  useFetchQuantifyReport,
  useIsSingleRecordQuery,
} from '@modules/quantify/hooks';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { CompareReportDataPoint } from '@modules/quantify/store/types';
import {
  getCompareQueryContainer,
  trackQuantifyCompareEvents,
} from '@modules/quantify/utils';
import { MergeSuggestionDetailsDrawer } from '@modules/taxonomy/components/MergeSuggestionDetailsDrawer';
import { EditReasonDrawer } from '@modules/taxonomy/components/Reason/EditReasonDrawer';
import { Box, Divider, List, Paper, Stack } from '@mui/material';
import { MouseEvent, useEffect, useState } from 'react';
import { useToggle } from 'react-use';
import { QueryContainer } from 'v2/core/services/notebook/types';
import TooltipWrapper from 'v2/views/components/charts/tooltip/TooltipWrapper';

interface CompareChartTooltipProps {
  showTooltip: boolean;
  setShowTooltip: (show: boolean) => void;
  tooltipPosition: TooltipPosition | null;
  setTooltipPosition: (position: TooltipPosition | null) => void;
  payload: CompareReportDataPoint | null;
  getLabelOptions?: GetChartLabelOptions;
  shouldShowAddToFilter?: boolean;
  shouldShowAddToFilterForBothCompareFields?: boolean;
}

export const CompareChartTooltip = ({
  payload,
  showTooltip,
  setShowTooltip,
  tooltipPosition,
  setTooltipPosition,
  getLabelOptions,
  shouldShowAddToFilter = true,
  shouldShowAddToFilterForBothCompareFields = true,
}: CompareChartTooltipProps) => {
  const query = useQuantifyStore(({ query }) => query);
  const analysisLink = useAnalysisStore(({ href }) => href);

  const [shouldOpenFeedbackDrawer, toggleOpenFeedbackDrawer] = useToggle(false);
  const [isCompareFieldA, setIsCompareFieldA] = useState(false);
  const [isSelectedMetricKeyInvisible, setIsSelectedMetricKeyInvisible] =
    useState(false);

  const [
    isSelectedMetricKeyUpdateInProgress,
    setIsSelectedMetricKeyUpdateInProgress,
  ] = useState(false);

  const [feedbackQueryContainer, setFeedbackQueryContainer] =
    useState<QueryContainer | null>(null);

  const setData = useChartTooltipStore(({ setData }) => setData);
  const shouldShowConfirmMergeDrawer = useChartTooltipStore(
    ({ shouldShowConfirmMergeDrawer }) => shouldShowConfirmMergeDrawer,
  );
  const activeMergeSuggestion = useChartTooltipStore(
    ({ activeMergeSuggestion }) => activeMergeSuggestion,
  );
  const shouldShowEditReasonDrawer = useChartTooltipStore(
    ({ shouldShowEditReasonDrawer }) => shouldShowEditReasonDrawer,
  );
  const activeReason = useChartTooltipStore(({ activeReason }) => activeReason);

  const isSingleRecordQuery = useIsSingleRecordQuery();

  const openFeedbackDrawer = ({ isFieldA }: { isFieldA: boolean }) => {
    setShowTooltip(false);
    setIsCompareFieldA(isFieldA);
    setIsSelectedMetricKeyInvisible(payload?.isInvisible ?? false);
    setIsSelectedMetricKeyUpdateInProgress(
      payload?.isUpdateInProgress ?? false,
    );

    const queryContainer = getCompareQueryContainer({
      query,
      isFieldA,
      metricKeyId: isSingleRecordQuery
        ? undefined
        : getChartLabelIdOrName(payload?.id),
      metricCategory: payload?.category,
    });

    setFeedbackQueryContainer(queryContainer);
    toggleOpenFeedbackDrawer();
    trackQuantifyCompareEvents({
      eventName: 'Quantify Compare - Opened Feedback Drawer',
      link: analysisLink,
      from: 'chart',
    });
  };

  useEffect(() => {
    if (query.groupBy) {
      setData({
        fieldName: getFieldNameFromEqlField(query.groupBy),
      });
    }
  }, [query.groupBy, setData]);

  useEffect(() => {
    if (payload) {
      setData({
        payload: {
          ...payload,
          id: getChartLabelIdOrName(payload.id),
          label: getChartLabelIdOrName(payload.label),
          value: 0, // won't be used
        },
      });
    }
  }, [payload, setData]);

  const onTooltipHover = (
    _: unknown,
    __: number | undefined,
    isActive: boolean,
    eve: MouseEvent<HTMLDivElement>,
  ) => {
    if (isActive) {
      setShowTooltip(true);
      setTooltipPosition({
        clientX: eve.clientX,
        clientY: eve.clientY,
      });
    } else {
      setShowTooltip(false);
      setTooltipPosition(null);
    }
  };

  useEffect(() => {
    if (shouldShowConfirmMergeDrawer || shouldShowEditReasonDrawer) {
      setShowTooltip(false);
    }
  }, [
    shouldShowConfirmMergeDrawer,
    setShowTooltip,
    shouldShowEditReasonDrawer,
  ]);

  const {
    onAddSuggestion,
    onRemoveSuggestion,
    onUpdateCategory,
    onUpdateSuggestedName,
  } = useMergeSuggestions();

  const fetchQuantifyReport = useFetchQuantifyReport();

  const shouldShowTooltip = showTooltip && payload && tooltipPosition;

  const shouldShowReasonActions =
    query.groupBy?.field === EqlFieldName.Reason && !payload?.isInvisible;

  return (
    <>
      {shouldShowTooltip && (
        <TooltipWrapper styles={tooltipPosition} onHoverHandle={onTooltipHover}>
          <Paper
            elevation={8}
            sx={{
              width: 300,
              borderRadius: 2,
              pt: 1.5,
              pb: shouldShowAddToFilter ? 0 : 1.5,
            }}
          >
            <Stack spacing={1.5}>
              <CompareChartTootlipLabel
                payload={payload}
                getLabelOptions={getLabelOptions}
              />
              <CompareChartTooltipDelta payload={payload} />
              <CompareChartTooltipRecordsCount
                payload={payload}
                onViewFeedback={openFeedbackDrawer}
              />
              {shouldShowAddToFilter && (
                <List disablePadding>
                  <Divider component="li" />
                  <CompareAddToFilterAction
                    metricKeyId={getChartLabelIdOrName(payload.id)}
                    metricCategory={payload.category}
                    shouldShowAddToFilterForBothCompareFields={
                      shouldShowAddToFilterForBothCompareFields
                    }
                  />
                  {shouldShowReasonActions && (
                    <Box>
                      <Divider component="li" />
                      <MergeWithAnotherReason />
                      <EditReason />
                    </Box>
                  )}
                </List>
              )}
            </Stack>
          </Paper>
        </TooltipWrapper>
      )}
      {shouldShowConfirmMergeDrawer && !!activeMergeSuggestion && (
        <MergeSuggestionDetailsDrawer
          activeMergeSuggestion={activeMergeSuggestion}
          closeDrawer={() => setData({ shouldShowConfirmMergeDrawer: false })}
          title="Merge Reasons"
          onUpdateSuggestedName={onUpdateSuggestedName}
          onUpdateCategory={onUpdateCategory}
          onAddSuggestion={onAddSuggestion}
          onRemoveSuggestion={onRemoveSuggestion}
          onMergeComplete={fetchQuantifyReport}
          drawerSource="Quantify Chart"
        />
      )}
      {shouldShowEditReasonDrawer && activeReason && (
        <EditReasonDrawer
          isOpen={shouldShowEditReasonDrawer}
          onClose={() => {
            setData({
              activeReason: null,
              shouldShowEditReasonDrawer: false,
            });
          }}
          reasonID={activeReason.id}
          activeReasonData={activeReason}
        />
      )}
      {payload && feedbackQueryContainer && (
        <QuantifyFeedbackListDrawer
          open={shouldOpenFeedbackDrawer}
          onClose={toggleOpenFeedbackDrawer}
          queryContainer={feedbackQueryContainer}
          isCompareFieldA={isCompareFieldA}
          metricKeyLabel={getChartLabelIdOrName(payload.label)}
          metricKeyId={getChartLabelIdOrName(payload.id)}
          metricKeyCategory={payload.category}
          isMetricKeyInvisible={isSelectedMetricKeyInvisible}
          isMetricKeyUpdateInProgress={isSelectedMetricKeyUpdateInProgress}
        />
      )}
    </>
  );
};
